import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";
import { selectAdminToken } from "../../admin/sessionSlice";
import { playerIdentity, postGame } from "../../services/gameApi";
import { themeAssetUrl } from "../../utils/themeAssets";
import "./game.css";

const BONUS_TEXT = "A diversity joke that is dignified";
const PLANS_URL = "https://extramileplay.com/plans";

function formatTimer(hours, minutes, seconds) {
  const h = hours ? (hours > 3 ? String(hours) : `0${hours}`) : "00";
  const m = minutes ? (minutes > 9 ? String(minutes) : `0${minutes}`) : "00";
  const s = seconds > 9 ? String(seconds) : `0${seconds}`;
  return `${h}:${m}:${s}`;
}

function isNarrow() {
  return window.innerWidth < 700;
}

function GamePage() {
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const adminToken = useSelector(selectAdminToken);
  const ballRef = useRef(null);
  const posRef = useRef(0);
  const promptsRef = useRef([]);
  const timeRef = useRef("00:00:00");
  const spinTimer = useRef(null);
  const typeTimer = useRef(null);
  const degreeRef = useRef(0);
  const spinningRef = useRef(false);
  const savingRef = useRef(false);
  const moveRef = useRef({ x: 0, y: 0, dx: 20, dy: 20 });

  const [loading, setLoading] = useState(true);
  const [board, setBoard] = useState(null);
  const [showStop, setShowStop] = useState(true);
  const [showNext, setShowNext] = useState(false);
  const [showOk, setShowOk] = useState(false);
  const [showBonus, setShowBonus] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [clock, setClock] = useState("00:00:00");

  const clampBall = useCallback(() => {
    const ball = ballRef.current;
    if (!ball) return;
    const header = document.querySelector(".upperaction");
    const topInset = header ? Math.ceil(header.getBoundingClientRect().bottom) : 0;
    const width = ball.offsetWidth;
    const height = ball.offsetHeight;
    const padX = (width * (Math.SQRT2 - 1)) / 2;
    const padY = (height * (Math.SQRT2 - 1)) / 2;
    const minLeft = padX;
    const maxLeft = Math.max(minLeft, window.innerWidth - width - padX);
    const minTop = topInset + padY;
    const maxTop = Math.max(minTop, window.innerHeight - height - padY);
    const motion = moveRef.current;
    if (motion.x > maxLeft) motion.dx = -Math.abs(motion.dx);
    if (motion.x < minLeft) motion.dx = Math.abs(motion.dx);
    if (motion.y > maxTop) motion.dy = -Math.abs(motion.dy);
    if (motion.y < minTop) motion.dy = Math.abs(motion.dy);
    motion.x = Math.min(maxLeft, Math.max(minLeft, motion.x));
    motion.y = Math.min(maxTop, Math.max(minTop, motion.y));
    ball.style.position = "fixed";
    ball.style.left = `${motion.x}px`;
    ball.style.top = `${motion.y}px`;
  }, []);

  const applyBallBox = useCallback((mode) => {
    const ball = ballRef.current;
    if (!ball) return;
    const narrow = isNarrow();
    ball.style.position = "fixed";
    if (mode === "stopped") {
      ball.style.width = narrow ? "200px" : "400px";
      ball.style.height = narrow ? "200px" : "400px";
    } else if (mode === "resume") {
      ball.style.width = narrow ? "100px" : "200px";
      ball.style.height = narrow ? "100px" : "300px";
    } else {
      ball.style.width = narrow ? "100px" : "200px";
      ball.style.height = narrow ? "100px" : "200px";
    }
    clampBall();
  }, [clampBall]);

  const clearTypeTimer = () => {
    if (typeTimer.current) {
      clearTimeout(typeTimer.current);
      typeTimer.current = null;
    }
  };

  const typeLine = (text, onDone) => {
    clearTypeTimer();
    setTyped("");
    let count = 0;
    const write = () => {
      count += 1;
      setTyped(text.slice(0, count));
      if (count < text.length) {
        typeTimer.current = setTimeout(write, 10);
      } else if (onDone) {
        onDone();
      }
    };
    typeTimer.current = setTimeout(write, 1500);
  };

  const finishSpin = useCallback(() => {
    spinningRef.current = false;
    if (spinTimer.current) clearTimeout(spinTimer.current);
    setShowBonus(true);
    setShowNext(false);
    setShowStop(false);
    applyBallBox("stopped");
  }, [applyBallBox]);

  const rotate = useCallback(() => {
    const ball = ballRef.current;
    if (!ball || !spinningRef.current) return;
    if (posRef.current >= promptsRef.current.length) {
      finishSpin();
      return;
    }
    const degree = degreeRef.current;
    ball.style.transform = `rotate(${degree}deg)`;
    spinTimer.current = setTimeout(() => {
      degreeRef.current = (degree + 1) % 360;
      rotate();
    }, 5);
  }, [finishSpin]);

  const startSpin = useCallback(() => {
    spinningRef.current = true;
    rotate();
  }, [rotate]);

  const saveGame = useCallback(async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    const { userId, firstName } = playerIdentity(user);
    try {
      const result = await postGame("/saveResult", adminToken, {
        userId,
        firstName,
        time: timeRef.current,
      });
      if (result.demo) {
        Swal.fire("Subscribe to any PLAN to play with your peers.", "", "success").then(() => {
          window.location.href = PLANS_URL;
        });
        return;
      }
      navigate("/thankyou");
    } catch (error) {
      savingRef.current = false;
      if (error?.status === 401) {
        Swal.fire("Session Expired!", "Session expired", "error").then(() => {
          window.location.href = process.env.REACT_APP_BASE_URL || "/";
        });
        return;
      }
      Swal.fire("Error", "Could not save your result. Please try again.", "error");
    }
  }, [adminToken, navigate, user]);

  useEffect(() => {
    if (!user) {
      navigate("/");
      return undefined;
    }
    const { userId } = playerIdentity(user);
    if (!adminToken || !userId) return undefined;
    let cancelled = false;
    postGame("/gameBoard", adminToken, { userId })
      .then((result) => {
        if (cancelled) return;
        if (result.completed) {
          navigate("/thankyou", { replace: true });
          return;
        }
        promptsRef.current = Array.isArray(result.prompts) ? result.prompts : [];
        setBoard(result);
        setLoading(false);
        if (result.tooFew) {
          Swal.fire({
            title: "Add more questions to complete this challenge",
            text: "Please check admin",
            background: "#fff",
          }).then(() => {
            navigate("/rules");
          });
        }
      })
      .catch((error) => {
        if (cancelled) return;
        setLoading(false);
        if (error?.status === 401) {
          Swal.fire("Session Expired!", "Session expired", "error").then(() => {
            window.location.href = process.env.REACT_APP_BASE_URL || "/";
          });
          return;
        }
        Swal.fire("Error", "Could not load the game. Please try again.", "error");
      });
    return () => {
      cancelled = true;
    };
  }, [adminToken, navigate, user]);

  useEffect(() => {
    if (!board) return undefined;
    applyBallBox("playing");
    startSpin();
    let hours = 0;
    let minutes = 0;
    let seconds = 0;
    const timer = setInterval(() => {
      seconds += 1;
      if (seconds >= 60) {
        seconds = 0;
        minutes += 1;
        if (minutes >= 60) {
          minutes = 0;
          hours += 1;
        }
      }
      const next = formatTimer(hours, minutes, seconds);
      timeRef.current = next;
      setClock(next);
    }, 1000);

    const bounce = setInterval(() => {
      const motion = moveRef.current;
      motion.x += motion.dx;
      motion.y += motion.dy;
      clampBall();
    }, 100);

    const onResize = () => {
      if (spinningRef.current) applyBallBox("playing");
      else clampBall();
    };
    window.addEventListener("resize", onResize);

    return () => {
      clearInterval(timer);
      clearInterval(bounce);
      spinningRef.current = false;
      if (spinTimer.current) clearTimeout(spinTimer.current);
      clearTypeTimer();
      window.removeEventListener("resize", onResize);
    };
  }, [applyBallBox, board, clampBall, startSpin]);

  const onStop = () => {
    setShowStop(false);
    spinningRef.current = false;
    if (spinTimer.current) clearTimeout(spinTimer.current);
    applyBallBox("stopped");
    window.setTimeout(() => setModalOpen(true), 1000);
    const prompts = promptsRef.current;
    if (posRef.current < prompts.length) {
      const prompt = prompts[posRef.current];
      posRef.current += 1;
      typeLine(prompt, () => setShowNext(true));
    }
  };

  const onNext = () => {
    if (posRef.current === 1 && user?.sessionId === "demobypass") {
      Swal.fire("Subscribe to any PLAN to play with your peers.", "", "success").then(() => {
        window.location.href = PLANS_URL;
      });
    }
    if (posRef.current >= promptsRef.current.length) {
      saveGame();
      return;
    }
    setShowStop(true);
    setTyped("");
    setShowNext(false);
    setModalOpen(false);
    applyBallBox("resume");
    startSpin();
  };

  const onBonus = () => {
    window.setTimeout(() => setModalOpen(true), 800);
    typeLine(BONUS_TEXT, () => setShowOk(true));
  };

  const onOk = () => {
    saveGame();
  };

  if (!user) return null;
  if (loading || !board) {
    return <p className="equity-game-message">Loading...</p>;
  }

  const buttonStyle = {
    color: board.buttonText_color,
    background: board.submit_color,
  };
  const ticket = board.assetUrls?.ticket || themeAssetUrl({ ticket: board.ticket, assetUrls: board.assetUrls }, "ticket");
  const cardImage = board.assetUrls?.all_icon_image
    || themeAssetUrl({ all_icon_image: board.all_icon_image, assetUrls: board.assetUrls }, "all_icon_image");

  return (
    <div className="equity-game">
      <div className={`prompt-overlay${modalOpen ? " is-open" : ""}`} />
      <div
        className={`prompt-modal${modalOpen ? " is-open" : ""}`}
        style={cardImage ? { backgroundImage: `url(${cardImage})` } : undefined}
      >
        <div className="prompt-content">
          <h4 className="correct-answer" style={{ color: board.text_color }}>{typed}</h4>
          <div className="prompt-actions">
            {showNext ? (
              <div className="submit-button" style={buttonStyle} onClick={onNext} role="button">Next</div>
            ) : null}
            {showOk ? (
              <div className="submit-button" style={buttonStyle} onClick={onOk} role="button">OK</div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="bonus-wrap">
        {showBonus ? (
          <>
            <img
              src="/php/images/Bonus.png"
              alt=""
              className="bonus-image"
              onClick={onBonus}
              onError={(event) => {
                event.currentTarget.style.display = "none";
                const fallback = event.currentTarget.nextElementSibling;
                if (fallback) fallback.style.display = "block";
              }}
            />
            <div className="bonus-fallback" style={buttonStyle} onClick={onBonus} role="button">Bonus</div>
          </>
        ) : null}
      </div>

      <div className="ball-stage">
        <img
          ref={ballRef}
          src={ticket || "/php/ball.svg"}
          className="ball-image"
          alt="Football"
          onError={(event) => {
            if (!event.currentTarget.src.endsWith("/php/ball.svg")) {
              event.currentTarget.src = "/php/ball.svg";
            }
          }}
        />
      </div>
      <div className="step-text" id="stepText" style={{ color: board.text_color }}>
        {board.step_1}
      </div>
      {showStop ? (
        <div style={{ textAlign: "center" }}>
          <div className="stop-button" style={buttonStyle} onClick={onStop} role="button">STOP</div>
        </div>
      ) : null}
      <input type="hidden" id="timer" value={clock} readOnly />
    </div>
  );
}

export default GamePage;
